import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Tabs,
  Tab,
  InputAdornment,
  Divider,
  Alert,
  Checkbox
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import ChatIcon from '@mui/icons-material/Chat';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import api from '../services/api';
import DealDetailModal from './DealDetailModal';
import BulkDeleteModal from './BulkDeleteModal';
import { useAuth } from '../contexts/AuthContext';

// Glassmorphism styles
const glassStyle = {
  background: 'rgba(255, 255, 255, 0.7)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
};

interface Stage {
  id: number;
  pipeline: number;
  pipeline_name: string;
  name: string;
  color: string;
  display_order: number;
  opportunities_count: number;
}

interface LeadTag {
  id: number;
  name: string;
  color: string;
}

interface LeadOption {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  company: string | null;
  tags?: LeadTag[];
  has_conversation?: boolean;
  conversation_id?: number;
}

interface Opportunity {
  id: number;
  internal_code: string;
  total_value: number;
  discount: number;
  addition: number;
  freight: number;
  final_value: number;
  status: string;
  loss_reason?: string;
  notes?: string;
  lead: number;
  lead_name: string;
  lead_email: string;
  lead_phone: string;
  lead_company: string;
  lead_tags?: LeadTag[];
  pipeline: { id: number; name: string };
  pipeline_name: string;
  stage: { id: number; name: string };
  stage_name: string;
  stage_color: string;
  attendant?: { id: number; name: string; email: string };
  status_display: string;
  created_at: string;
  last_moved_at: string;
  products?: any[];
}

interface KanbanBoardProps {
  pipelineId?: number;
}

const KanbanBoard: React.FC<KanbanBoardProps> = ({ pipelineId }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pipelines, setPipelines] = useState<any[]>([]);
  const [selectedPipeline, setSelectedPipeline] = useState<number | null>(null);
  const [stages, setStages] = useState<Stage[]>([]);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Scroll state
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Modals
  const [showOpportunityModal, setShowOpportunityModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState<Opportunity | null>(null);

  // Forms
  const [opportunityForm, setOpportunityForm] = useState({
    lead_id: '',
    total_value: 0,
    discount: 0,
    addition: 0,
    freight: 0,
    notes: '',
  });

  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    sku: '',
    price: 0,
    quantity: 1,
  });

  // New Deal Modal States
  const [dealModalTab, setDealModalTab] = useState(0); // 0 = select lead, 1 = create new
  const [leadSearchResults, setLeadSearchResults] = useState<LeadOption[]>([]);
  const [selectedLead, setSelectedLead] = useState<LeadOption | null>(null);
  const [searchingLeads, setSearchingLeads] = useState(false);
  const [creatingDeal, setCreatingDeal] = useState(false);
  const [newLeadForm, setNewLeadForm] = useState({
    name: '',
    phone: '',
    email: '',
    company: ''
  });
  const [dealValue, setDealValue] = useState<number>(0);
  const [dealNotes, setDealNotes] = useState('');
  const [sendWelcomeMessage, setSendWelcomeMessage] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState('');

  // Bulk selection states
  const [selectedOpportunities, setSelectedOpportunities] = useState<Set<number>>(new Set());
  const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false);
  const [companyUsers, setCompanyUsers] = useState<any[]>([]);
  const [filterUser, setFilterUser] = useState<string>('all');

  useEffect(() => {
    loadPipelines();
    loadCompanyUsers();
  }, []);

  useEffect(() => {
    if (selectedPipeline) {
      loadStages(selectedPipeline);
      loadOpportunities(selectedPipeline);
    }
  }, [selectedPipeline]);

  useEffect(() => {
    if (selectedPipeline) loadOpportunities(selectedPipeline);
  }, [filterUser]);

  const loadCompanyUsers = async () => {
    try {
      const response = await api.get('/auth/users/company_users/');
      const users = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setCompanyUsers(users);
    } catch (err) {
      console.error('Erro ao carregar usuários:', err);
    }
  };

  // Check scroll position
  const checkScrollPosition = useCallback(() => {
    const container = scrollContainerRef.current;
    if (container) {
      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(
        container.scrollLeft < container.scrollWidth - container.clientWidth - 10
      );
    }
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      checkScrollPosition();
      container.addEventListener('scroll', checkScrollPosition);
      window.addEventListener('resize', checkScrollPosition);
      return () => {
        container.removeEventListener('scroll', checkScrollPosition);
        window.removeEventListener('resize', checkScrollPosition);
      };
    }
  }, [stages, checkScrollPosition]);

  // Drag to scroll handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Only start drag if clicking on the container background, not on cards
    if ((e.target as HTMLElement).closest('[draggable="true"]')) return;

    setIsDragging(true);
    setStartX(e.pageX - container.offsetLeft);
    setScrollLeft(container.scrollLeft);
    container.style.cursor = 'grabbing';
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    const container = scrollContainerRef.current;
    if (!container) return;

    e.preventDefault();
    const x = e.pageX - container.offsetLeft;
    const walk = (x - startX) * 1.5; // Scroll speed multiplier
    container.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    const container = scrollContainerRef.current;
    if (container) {
      container.style.cursor = 'grab';
    }
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
      const container = scrollContainerRef.current;
      if (container) {
        container.style.cursor = 'grab';
      }
    }
  };

  // Arrow scroll handlers
  const scrollByAmount = (direction: 'left' | 'right') => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = 320; // Approximately one column width
    const newScrollLeft = direction === 'left'
      ? container.scrollLeft - scrollAmount
      : container.scrollLeft + scrollAmount;

    container.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth'
    });
  };

  const loadPipelines = async () => {
    try {
      setLoading(true);
      const response = await api.get('/pipelines/pipelines/?page_size=9999');
      const pipelinesArray = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setPipelines(pipelinesArray);

      if (pipelineId) {
        setSelectedPipeline(pipelineId);
      } else if (pipelinesArray.length > 0) {
        setSelectedPipeline(pipelinesArray[0].id);
      }
    } catch (err: any) {
      setError('Erro ao carregar pipelines');
      setPipelines([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStages = async (pipelineId: number) => {
    try {
      const response = await api.get(`/pipelines/stages/?pipeline=${pipelineId}&page_size=9999`);
      const stagesArray = Array.isArray(response.data) ? response.data : (response.data.results || []);
      const pipelineStages = stagesArray.filter((stage: Stage) => stage.pipeline === pipelineId);
      setStages(pipelineStages.sort((a: Stage, b: Stage) => a.display_order - b.display_order));
    } catch (err) {
      console.error('Erro ao carregar estágios:', err);
    }
  };

  const loadOpportunities = async (pipelineId: number) => {
    try {
      let url = `/deals/deals/?pipeline=${pipelineId}&page_size=9999`;
      if (filterUser && filterUser !== 'all') url += `&owner=${filterUser}`;
      const response = await api.get(url);
      const oppsArray = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setOpportunities(oppsArray);
    } catch (err) {
      console.error('Erro ao carregar oportunidades:', err);
    }
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, opportunity: Opportunity) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('opportunity', JSON.stringify(opportunity));
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>, stageId: number) => {
    e.preventDefault();
    const opportunityData = e.dataTransfer.getData('opportunity');
    if (!opportunityData) return;

    const opportunity: Opportunity = JSON.parse(opportunityData);
    if (opportunity.stage.id === stageId) return;

    try {
      await api.patch(`/deals/deals/${opportunity.id}/`, { stage: stageId });
      setOpportunities(prev =>
        prev.map(opp =>
          opp.id === opportunity.id
            ? { ...opp, stage: stages.find(s => s.id === stageId) || opp.stage }
            : opp
        )
      );
      loadStages(selectedPipeline!);
    } catch (err) {
      console.error('Erro ao mover oportunidade:', err);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleUpdateOpportunity = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOpportunity) return;

    try {
      await api.patch(`/opportunities/opportunities/${selectedOpportunity.id}/`, {
        lead: parseInt(opportunityForm.lead_id),
        total_value: opportunityForm.total_value,
        discount: opportunityForm.discount,
        addition: opportunityForm.addition,
        freight: opportunityForm.freight,
        notes: opportunityForm.notes,
      });

      setShowEditModal(false);
      resetOpportunityForm();
      if (selectedPipeline) loadOpportunities(selectedPipeline);

      const response = await api.get(`/opportunities/opportunities/${selectedOpportunity.id}/`);
      setSelectedOpportunity(response.data);
    } catch (err) {
      console.error('Erro ao atualizar oportunidade:', err);
    }
  };

  const handleDeleteOpportunity = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja deletar esta oportunidade?')) return;

    try {
      await api.delete(`/deals/deals/${id}/`);
      setShowDetailModal(false);
      setSelectedOpportunity(null);
      if (selectedPipeline) {
        loadOpportunities(selectedPipeline);
        loadStages(selectedPipeline);
      }
    } catch (err) {
      console.error('Erro ao deletar oportunidade:', err);
    }
  };

  const handleAddProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOpportunity) return;

    try {
      await api.post('/opportunities/products/', {
        ...productForm,
        opportunity: selectedOpportunity.id,
      });
      setShowProductModal(false);
      setProductForm({ name: '', description: '', sku: '', price: 0, quantity: 1 });

      const response = await api.get(`/opportunities/opportunities/${selectedOpportunity.id}/`);
      setSelectedOpportunity(response.data);
    } catch (err) {
      console.error('Erro ao adicionar produto:', err);
    }
  };

  // Search leads for the new deal modal
  const searchLeads = async (query: string) => {
    if (!query || query.length < 2) {
      setLeadSearchResults([]);
      return;
    }
    setSearchingLeads(true);
    try {
      const response = await api.get(`/leads/leads/?search=${encodeURIComponent(query)}`);
      const leads = response.data.results || response.data || [];

      // Check for conversations for each lead
      const leadsWithConversations = await Promise.all(
        leads.slice(0, 10).map(async (lead: any) => {
          try {
            const convResponse = await api.get(`/messaging/conversations/?lead=${lead.id}`);
            const conversations = convResponse.data.results || convResponse.data || [];
            return {
              ...lead,
              has_conversation: conversations.length > 0,
              conversation_id: conversations.length > 0 ? conversations[0].id : null
            };
          } catch {
            return { ...lead, has_conversation: false, conversation_id: null };
          }
        })
      );

      setLeadSearchResults(leadsWithConversations);
    } catch (err) {
      console.error('Error searching leads:', err);
      setLeadSearchResults([]);
    } finally {
      setSearchingLeads(false);
    }
  };

  // Reset the new deal modal
  const resetNewDealModal = () => {
    setDealModalTab(0);
    setLeadSearchResults([]);
    setSelectedLead(null);
    setNewLeadForm({ name: '', phone: '', email: '', company: '' });
    setDealValue(0);
    setDealNotes('');
    setSendWelcomeMessage(false);
    setWelcomeMessage('');
  };

  // Create a new deal with the improved flow
  const handleCreateNewDeal = async () => {
    if (!selectedPipeline || stages.length === 0) return;

    setCreatingDeal(true);
    let conversationCreated = false;
    let conversationError = '';
    let shouldCreateConversation = false;

    try {
      let leadId: number;
      let isNewLead = false;

      if (dealModalTab === 0 && selectedLead) {
        // Using existing lead
        leadId = selectedLead.id;
      } else if (dealModalTab === 1 && newLeadForm.name && newLeadForm.phone) {
        // Creating new lead
        isNewLead = true;
        shouldCreateConversation = true;

        try {
          const leadResponse = await api.post('/leads/leads/', {
            name: newLeadForm.name,
            phone: newLeadForm.phone,
            email: newLeadForm.email || null,
            company: newLeadForm.company || null,
            source: 'whatsapp',
            status: 'new'
          });
          leadId = leadResponse.data.id;
        } catch (leadErr: any) {
          // Check if it's a duplicate phone error
          if (leadErr.response?.data?.error === 'duplicate_phone') {
            const existingLeadId = leadErr.response.data.existing_lead_id;
            const existingLeadName = leadErr.response.data.existing_lead_name;

            const useExisting = window.confirm(
              `Já existe um lead com este telefone: "${existingLeadName}"\n\nDeseja criar a negociação usando este lead existente?`
            );

            if (useExisting && existingLeadId) {
              leadId = existingLeadId;
              isNewLead = false;
              shouldCreateConversation = false; // Existing lead may already have conversation
            } else {
              setCreatingDeal(false);
              return;
            }
          } else {
            throw leadErr;
          }
        }

        // Create a contact and conversation only for NEW leads
        if (isNewLead && newLeadForm.phone) {
          try {
            // First create a Contact
            console.log('Creating contact for lead:', leadId);
            const contactResponse = await api.post('/messaging/contacts/', {
              lead: leadId,
              name: newLeadForm.name,
              phone: newLeadForm.phone,
              whatsapp: newLeadForm.phone,
              email: newLeadForm.email || null,
              channel: 'whatsapp',
              channel_identifier: newLeadForm.phone,
              is_primary: true
            });
            console.log('Contact created:', contactResponse.data);

            // Then create a Conversation with the contact
            console.log('Creating conversation for contact:', contactResponse.data.id);
            const convResponse = await api.post('/messaging/conversations/', {
              contact: contactResponse.data.id,
              channel: 'whatsapp',
              status: 'active'
            });
            console.log('Conversation created:', convResponse.data);
            conversationCreated = true;

            // Send welcome message if requested
            if (sendWelcomeMessage && welcomeMessage.trim()) {
              console.log('Sending welcome message');
              await api.post('/messaging/messages/', {
                conversation: convResponse.data.id,
                content: welcomeMessage.trim(),
                direction: 'outbound',
                status: 'pending'
              });
              console.log('Welcome message sent');
            }
          } catch (convErr: any) {
            console.error('Error creating contact/conversation:', convErr);
            conversationError = convErr.response?.data?.detail ||
                               convErr.response?.data?.error ||
                               JSON.stringify(convErr.response?.data) ||
                               convErr.message ||
                               'Erro desconhecido';
            // Continue anyway - deal creation is more important
          }
        }
      } else {
        alert('Por favor, selecione um lead ou preencha os dados do novo lead.');
        setCreatingDeal(false);
        return;
      }

      // Create the deal
      const firstStage = stages[0];
      await api.post('/deals/deals/', {
        lead: leadId,
        pipeline: selectedPipeline,
        stage: firstStage.id,
        name: dealModalTab === 0 && selectedLead
          ? `${selectedLead.name} - ${new Date().toLocaleDateString('pt-BR')}`
          : `${newLeadForm.name} - ${new Date().toLocaleDateString('pt-BR')}`,
        value: dealValue || 0,
        status: 'open',
        description: dealNotes,
        owner: user?.id  // Vincular ao usuário que está criando
      });

      // Close modal and refresh
      setShowOpportunityModal(false);
      resetNewDealModal();
      if (selectedPipeline) loadOpportunities(selectedPipeline);

      // Show warning if conversation creation failed
      if (shouldCreateConversation && !conversationCreated) {
        alert(`Negociação criada com sucesso!\n\nPorém, não foi possível criar a conversa WhatsApp automaticamente.\nErro: ${conversationError}\n\nVocê pode iniciar a conversa manualmente na página de Mensagens.`);
      }

    } catch (err: any) {
      console.error('Error creating deal:', err);
      const errorData = err.response?.data;
      let errorMessage = 'Erro ao criar negociação';

      if (errorData) {
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.name) {
          errorMessage = `Nome: ${errorData.name}`;
        } else if (errorData.non_field_errors) {
          errorMessage = errorData.non_field_errors.join(', ');
        }
      }

      alert(errorMessage);
    } finally {
      setCreatingDeal(false);
    }
  };

  const resetOpportunityForm = () => {
    setOpportunityForm({
      lead_id: '',
      total_value: 0,
      discount: 0,
      addition: 0,
      freight: 0,
      notes: '',
    });
    resetNewDealModal();
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ganho': return '#22c55e';
      case 'perdido': return '#ef4444';
      default: return '#6366f1';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ganho': return 'Ganho';
      case 'perdido': return 'Perdido';
      default: return 'Em Processo';
    }
  };

  // Calcula dias desde a última movimentação (aging)
  const getDaysInStage = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Retorna cor baseada no aging (verde = recente, amarelo = atenção, vermelho = crítico)
  const getAgingColor = (days: number) => {
    if (days <= 3) return { bg: 'rgba(34, 197, 94, 0.1)', color: '#22c55e', label: 'Recente' };
    if (days <= 7) return { bg: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', label: 'Atenção' };
    return { bg: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', label: 'Estagnado' };
  };

  // Gera iniciais para o avatar
  const getInitials = (name: string) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Gera cor consistente baseada no nome
  const getAvatarColor = (name: string) => {
    const colors = [
      '#6366f1', '#8b5cf6', '#ec4899', '#f43f5e',
      '#f97316', '#eab308', '#22c55e', '#14b8a6',
      '#06b6d4', '#3b82f6'
    ];
    const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  // Ações rápidas de contato
  // Bulk selection handlers
  const toggleSelectOpportunity = (id: number) => {
    setSelectedOpportunities(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const allIds = opportunities.map(o => o.id);
    const allSelected = allIds.every(id => selectedOpportunities.has(id));
    setSelectedOpportunities(prev => {
      const next = new Set(prev);
      allIds.forEach(id => allSelected ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const handleBulkDeleteComplete = () => {
    setIsBulkDeleteOpen(false);
    setSelectedOpportunities(new Set());
    if (selectedPipeline) {
      loadOpportunities(selectedPipeline);
      loadStages(selectedPipeline);
    }
  };

  const handleQuickContact = async (e: React.MouseEvent, type: string, value: string, leadId?: number, leadName?: string) => {
    e.stopPropagation();

    if (type === 'whatsapp' && leadId && value) {
      const phone = value.replace(/\D/g, '');
      const contactName = leadName || 'Lead';

      console.log('WhatsApp quick contact - Lead:', leadId, 'Phone:', phone);

      try {
        let existingConversationId: number | null = null;

        // 1. PRIORIDADE MÁXIMA: Buscar conversa diretamente pelo LEAD
        // O backend filtra por contact__lead_id
        console.log('Buscando conversa diretamente pelo lead:', leadId);
        try {
          const convResponse = await api.get(`/messaging/conversations/?lead=${leadId}&channel=whatsapp`);
          const conversations = convResponse.data.results || convResponse.data || [];

          if (conversations.length > 0) {
            existingConversationId = conversations[0].id;
            console.log('Conversa encontrada pelo lead:', existingConversationId);
          }
        } catch (e) {
          console.log('Busca por lead falhou:', e);
        }

        // 2. Se não encontrou pelo lead, criar contact e conversa
        if (!existingConversationId) {
          console.log('Nenhuma conversa encontrada para o lead, criando nova...');

          // Verificar se já existe um contact para este lead
          let contactId: number | null = null;
          try {
            const contactResponse = await api.get(`/messaging/contacts/?lead=${leadId}&channel=whatsapp`);
            const contacts = contactResponse.data.results || contactResponse.data || [];

            if (contacts.length > 0) {
              contactId = contacts[0].id;
              console.log('Contact existente encontrado:', contactId);
            }
          } catch (e) {
            console.log('Busca de contact por lead falhou:', e);
          }

          // Se não tem contact, criar um novo
          if (!contactId) {
            console.log('Criando novo contact...');
            const newContactResponse = await api.post('/messaging/contacts/', {
              lead: leadId,
              name: contactName,
              phone: phone,
              whatsapp: phone,
              channel: 'whatsapp',
              channel_identifier: phone,
              is_primary: true
            });
            contactId = newContactResponse.data.id;
            console.log('Novo contact criado:', contactId);
          }

          // Criar conversa para o contact
          if (contactId) {
            console.log('Criando nova conversa para contact:', contactId);
            const newConvResponse = await api.post('/messaging/conversations/', {
              contact: contactId,
              channel: 'whatsapp',
              external_id: phone,
              status: 'active'
            });
            existingConversationId = newConvResponse.data.id;
            console.log('Nova conversa criada:', existingConversationId);
          }
        }

        // 3. Navegar para a conversa
        if (existingConversationId) {
          navigate(`/messaging?conversation=${existingConversationId}`);
        } else {
          console.error('Não foi possível encontrar ou criar conversa');
          window.open(`https://wa.me/${phone}`, '_blank');
        }
      } catch (err: any) {
        console.error('Error handling WhatsApp contact:', err);
        // Fallback: abrir WhatsApp externo
        window.open(`https://wa.me/${phone}`, '_blank');
      }
    } else if (type === 'whatsapp' && value) {
      // Fallback: abre WhatsApp externo se não tiver leadId
      const phone = value.replace(/\D/g, '');
      window.open(`https://wa.me/${phone}`, '_blank');
    } else if (type === 'email' && value) {
      window.open(`mailto:${value}`, '_blank');
    } else if (type === 'phone' && value) {
      window.open(`tel:${value}`, '_blank');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 4 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (pipelines.length === 0) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        p: 4,
        textAlign: 'center'
      }}>
        <Typography variant="h1" sx={{ fontSize: 64, mb: 2 }}>📊</Typography>
        <Typography variant="h6" sx={{ color: '#374151', mb: 1 }}>
          Nenhum pipeline configurado
        </Typography>
        <Typography variant="body2" sx={{ color: '#6b7280' }}>
          Crie um pipeline para organizar suas vendas
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      width: '100%',
      minWidth: 0, // Importante para flex respeitar limites
      minHeight: 0,
      overflow: 'hidden',
      p: 2
    }}>
      {/* Pipeline Selector & Actions */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 2,
        pb: 2,
        borderBottom: '1px solid rgba(0,0,0,0.05)'
      }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Pipeline</InputLabel>
            <Select
              value={selectedPipeline || ''}
              onChange={(e) => setSelectedPipeline(Number(e.target.value))}
              label="Pipeline"
              sx={{
                background: 'rgba(255,255,255,0.8)',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0,0,0,0.1)'
                }
              }}
            >
              {pipelines.map(pipeline => (
                <MenuItem key={pipeline.id} value={pipeline.id}>
                  {pipeline.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {companyUsers.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Atendente</InputLabel>
              <Select
                value={filterUser}
                onChange={(e) => setFilterUser(e.target.value)}
                label="Atendente"
                sx={{
                  background: 'rgba(255,255,255,0.8)',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0,0,0,0.1)'
                  }
                }}
              >
                <MenuItem value="all">Todos</MenuItem>
                {companyUsers.map((u: any) => (
                  <MenuItem key={u.id} value={String(u.id)}>
                    {u.get_full_name || u.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          {opportunities.length > 0 && (
            <Button
              onClick={toggleSelectAll}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 13,
                color: '#374151',
                background: 'rgba(255,255,255,0.8)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(0,0,0,0.05)',
                borderRadius: 2,
                px: 2.5,
                py: 1,
                '&:hover': {
                  background: 'rgba(255,255,255,0.95)',
                  borderColor: '#6366f1'
                }
              }}
            >
              {opportunities.every(o => selectedOpportunities.has(o.id)) ? 'Desmarcar tudo' : 'Selecionar tudo'}
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              resetOpportunityForm();
              setShowOpportunityModal(true);
            }}
            sx={{
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              boxShadow: '0 4px 15px rgba(99,102,241,0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #4f46e5, #7c3aed)'
              }
            }}
          >
            Nova Negociação
          </Button>
        </Box>
      </Box>

      {/* Kanban Columns */}
      {!selectedPipeline ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography color="text.secondary">Selecione um pipeline para visualizar</Typography>
        </Box>
      ) : (
        <Box sx={{
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
          minWidth: 0 // Importante para flex container respeitar limites
        }}>
          {/* Left Arrow */}
          {canScrollLeft && (
            <IconButton
              onClick={() => scrollByAmount('left')}
              sx={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 10,
                width: 44,
                height: 44,
                background: 'rgba(255,255,255,0.95)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                border: '1px solid rgba(0,0,0,0.05)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  color: '#fff'
                }
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
          )}

          {/* Right Arrow */}
          {canScrollRight && (
            <IconButton
              onClick={() => scrollByAmount('right')}
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 10,
                width: 44,
                height: 44,
                background: 'rgba(255,255,255,0.95)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                border: '1px solid rgba(0,0,0,0.05)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  color: '#fff'
                }
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          )}

          {/* Scrollable Container */}
          <Box
            ref={scrollContainerRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              gap: 2,
              overflowX: 'auto',
              overflowY: 'hidden',
              pb: 2,
              px: 1,
              cursor: isDragging ? 'grabbing' : 'grab',
              userSelect: isDragging ? 'none' : 'auto',
              '&::-webkit-scrollbar': { height: 8 },
              '&::-webkit-scrollbar-track': { background: 'rgba(0,0,0,0.02)', borderRadius: 4 },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(99,102,241,0.3)',
                borderRadius: 4,
                '&:hover': {
                  background: 'rgba(99,102,241,0.5)'
                }
              }
            }}
          >
          {stages.map((stage) => {
            const stageOpportunities = opportunities.filter(opp => opp.stage && opp.stage.id === stage.id);
            const totalValue = stageOpportunities.reduce((sum, opp) => sum + opp.final_value, 0);

            return (
              <Box
                key={stage.id}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, stage.id)}
                sx={{
                  minWidth: 300,
                  width: 300,
                  maxWidth: 300,
                  flexShrink: 0, // Não permite que a coluna encolha
                  display: 'flex',
                  flexDirection: 'column',
                  background: 'rgba(255,255,255,0.5)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  border: '1px solid rgba(0,0,0,0.05)',
                  overflow: 'hidden',
                  height: 'fit-content',
                  maxHeight: '100%'
                }}
              >
                {/* Column Header */}
                <Box sx={{
                  p: 2,
                  borderBottom: '1px solid rgba(0,0,0,0.05)',
                  borderTop: `4px solid ${stage.color}`
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1f2937' }}>
                      {stage.name}
                    </Typography>
                    <Chip
                      label={stageOpportunities.length}
                      size="small"
                      sx={{
                        height: 24,
                        minWidth: 24,
                        background: 'rgba(0,0,0,0.05)',
                        fontWeight: 700,
                        fontSize: 12
                      }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ color: '#22c55e', fontWeight: 600 }}>
                    {formatCurrency(totalValue)}
                  </Typography>
                </Box>

                {/* Cards Area */}
                <Box sx={{
                  flex: 1,
                  p: 1.5,
                  overflowY: 'auto',
                  minHeight: 200,
                  '&::-webkit-scrollbar': { width: 4 },
                  '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.1)', borderRadius: 2 }
                }}>
                  {stageOpportunities.length === 0 ? (
                    <Box sx={{
                      p: 3,
                      textAlign: 'center',
                      border: '2px dashed rgba(0,0,0,0.1)',
                      borderRadius: 2,
                      color: '#9ca3af'
                    }}>
                      <Typography variant="body2">
                        Arraste oportunidades para cá
                      </Typography>
                    </Box>
                  ) : (
                    stageOpportunities.map((opp) => {
                      const daysInStage = getDaysInStage(opp.last_moved_at);
                      const agingInfo = getAgingColor(daysInStage);
                      const leadName = opp.lead_name || 'Lead não encontrado';

                      return (
                        <Box
                          key={opp.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, opp)}
                          onClick={() => {
                            setSelectedOpportunity(opp);
                            setShowDetailModal(true);
                          }}
                          sx={{
                            mb: 1,
                            borderRadius: 2,
                            background: 'rgba(255,255,255,0.95)',
                            backdropFilter: 'blur(10px)',
                            border: opp.status === 'ganho' ? '2px solid #22c55e' :
                                    opp.status === 'perdido' ? '2px solid #ef4444' :
                                    '1px solid rgba(0,0,0,0.06)',
                            cursor: 'grab',
                            transition: 'all 0.2s ease',
                            overflow: 'hidden',
                            position: 'relative',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
                              '& .quick-actions': {
                                opacity: 1
                              }
                            },
                            '&:active': { cursor: 'grabbing' }
                          }}
                        >
                          {/* Checkbox for bulk selection */}
                          <Checkbox
                            checked={selectedOpportunities.has(opp.id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              toggleSelectOpportunity(opp.id);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              zIndex: 2,
                              padding: 0.5,
                              '& .MuiSvgIcon-root': { fontSize: 18 },
                              color: 'rgba(0,0,0,0.3)',
                              '&.Mui-checked': {
                                color: '#6366f1'
                              }
                            }}
                          />

                          {/* Header compacto com valor e status */}
                          <Box sx={{
                            px: 1.5,
                            py: 1,
                            pr: 4.5, // Add padding to avoid overlap with checkbox
                            background: opp.status === 'ganho'
                              ? 'linear-gradient(135deg, rgba(34,197,94,0.1), rgba(34,197,94,0.05))'
                              : opp.status === 'perdido'
                              ? 'linear-gradient(135deg, rgba(239,68,68,0.1), rgba(239,68,68,0.05))'
                              : 'linear-gradient(135deg, rgba(99,102,241,0.08), rgba(139,92,246,0.04))',
                            borderBottom: '1px solid rgba(0,0,0,0.04)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <Typography sx={{
                              fontSize: 15,
                              fontWeight: 700,
                              color: opp.status === 'ganho' ? '#16a34a' :
                                     opp.status === 'perdido' ? '#dc2626' : '#1f2937'
                            }}>
                              {formatCurrency(opp.final_value)}
                            </Typography>
                            {opp.status !== 'open' && (
                              <Chip
                                label={getStatusLabel(opp.status)}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: 9,
                                  fontWeight: 700,
                                  background: getStatusColor(opp.status),
                                  color: '#fff',
                                  borderRadius: 1
                                }}
                              />
                            )}
                          </Box>

                          {/* Conteúdo principal compacto */}
                          <Box sx={{ px: 1.5, py: 1 }}>
                            {/* Lead info com avatar pequeno */}
                            <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                              <Avatar
                                sx={{
                                  width: 28,
                                  height: 28,
                                  fontSize: 11,
                                  fontWeight: 700,
                                  background: getAvatarColor(leadName),
                                  flexShrink: 0
                                }}
                              >
                                {getInitials(leadName)}
                              </Avatar>
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography sx={{
                                  fontWeight: 600,
                                  color: '#1f2937',
                                  fontSize: 12,
                                  lineHeight: 1.2,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {leadName}
                                </Typography>
                                {opp.lead_company && (
                                  <Typography sx={{
                                    color: '#9ca3af',
                                    fontSize: 10,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {opp.lead_company}
                                  </Typography>
                                )}
                              </Box>
                            </Box>

                            {/* Lead Tags */}
                            {opp.lead_tags && opp.lead_tags.length > 0 && (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.75 }}>
                                {opp.lead_tags.slice(0, 3).map((tag) => (
                                  <Chip
                                    key={tag.id}
                                    label={tag.name}
                                    size="small"
                                    sx={{
                                      height: 16,
                                      fontSize: 9,
                                      fontWeight: 500,
                                      background: `${tag.color}20`,
                                      color: tag.color,
                                      border: `1px solid ${tag.color}40`,
                                      '& .MuiChip-label': { px: 0.75 }
                                    }}
                                  />
                                ))}
                                {opp.lead_tags.length > 3 && (
                                  <Chip
                                    label={`+${opp.lead_tags.length - 3}`}
                                    size="small"
                                    sx={{
                                      height: 16,
                                      fontSize: 9,
                                      fontWeight: 500,
                                      background: 'rgba(0,0,0,0.05)',
                                      color: '#6b7280',
                                      '& .MuiChip-label': { px: 0.5 }
                                    }}
                                  />
                                )}
                              </Box>
                            )}

                            {/* Footer compacto com ações, aging e responsável */}
                            <Box sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              pt: 0.75,
                              borderTop: '1px solid rgba(0,0,0,0.04)'
                            }}>
                              {/* Quick actions */}
                              <Box className="quick-actions" sx={{ display: 'flex', gap: 0.25, opacity: 0.6, transition: 'opacity 0.2s' }}>
                                {opp.lead_phone && (
                                  <Tooltip title="WhatsApp" arrow placement="top">
                                    <IconButton
                                      size="small"
                                      onClick={(e) => handleQuickContact(e, 'whatsapp', opp.lead_phone, opp.lead, opp.lead_name)}
                                      sx={{ width: 22, height: 22, color: '#25d366', '&:hover': { background: 'rgba(37,211,102,0.1)' } }}
                                    >
                                      <WhatsAppIcon sx={{ fontSize: 13 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {opp.lead_email && (
                                  <Tooltip title="Email" arrow placement="top">
                                    <IconButton
                                      size="small"
                                      onClick={(e) => handleQuickContact(e, 'email', opp.lead_email)}
                                      sx={{ width: 22, height: 22, color: '#6366f1', '&:hover': { background: 'rgba(99,102,241,0.1)' } }}
                                    >
                                      <EmailIcon sx={{ fontSize: 13 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {opp.lead_phone && (
                                  <Tooltip title="Ligar" arrow placement="top">
                                    <IconButton
                                      size="small"
                                      onClick={(e) => handleQuickContact(e, 'phone', opp.lead_phone)}
                                      sx={{ width: 22, height: 22, color: '#3b82f6', '&:hover': { background: 'rgba(59,130,246,0.1)' } }}
                                    >
                                      <PhoneIcon sx={{ fontSize: 13 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>

                              {/* Aging + Responsável */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Tooltip title={`${daysInStage}d neste estágio`} arrow placement="top">
                                  <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.25,
                                    px: 0.5,
                                    py: 0.25,
                                    borderRadius: 0.5,
                                    background: agingInfo.bg
                                  }}>
                                    <AccessTimeIcon sx={{ fontSize: 10, color: agingInfo.color }} />
                                    <Typography sx={{ fontSize: 9, fontWeight: 600, color: agingInfo.color }}>
                                      {daysInStage}d
                                    </Typography>
                                  </Box>
                                </Tooltip>
                                {opp.attendant && (
                                  <Tooltip title={opp.attendant.name} arrow placement="top">
                                    <Avatar sx={{ width: 18, height: 18, fontSize: 8, fontWeight: 700, background: getAvatarColor(opp.attendant.name) }}>
                                      {getInitials(opp.attendant.name)}
                                    </Avatar>
                                  </Tooltip>
                                )}
                              </Box>
                            </Box>
                          </Box>

                          {/* Loss Reason compacto */}
                          {opp.status === 'perdido' && opp.loss_reason && (
                            <Box sx={{
                              mx: 1.5,
                              mb: 1,
                              px: 1,
                              py: 0.5,
                              background: 'rgba(239,68,68,0.08)',
                              borderRadius: 1
                            }}>
                              <Typography sx={{ fontSize: 9, color: '#dc2626', lineHeight: 1.3 }}>
                                {opp.loss_reason}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      );
                    })
                  )}
                </Box>
              </Box>
            );
          })}
          </Box>
        </Box>
      )}

      {/* Create New Deal Modal */}
      <Dialog
        open={showOpportunityModal}
        onClose={() => {
          setShowOpportunityModal(false);
          resetNewDealModal();
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            ...glassStyle,
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)'
          }
        }}
      >
        {/* Header */}
        <Box sx={{
          p: 2.5,
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          background: 'linear-gradient(135deg, rgba(99,102,241,0.08), rgba(139,92,246,0.04))'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <AddIcon sx={{ color: 'white', fontSize: 22 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1f2937' }}>
                  Nova Negociação
                </Typography>
                <Typography variant="caption" sx={{ color: '#6b7280' }}>
                  Vincule a um lead existente ou crie um novo
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={() => {
                setShowOpportunityModal(false);
                resetNewDealModal();
              }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Tabs */}
        <Tabs
          value={dealModalTab}
          onChange={(_, v) => setDealModalTab(v)}
          sx={{
            px: 2,
            borderBottom: '1px solid rgba(0,0,0,0.06)',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              minHeight: 48
            }
          }}
        >
          <Tab
            icon={<SearchIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="Selecionar Lead"
          />
          <Tab
            icon={<PersonIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="Criar Novo Lead"
          />
        </Tabs>

        <DialogContent sx={{ p: 2.5 }}>
          {/* Tab 0: Select Existing Lead */}
          {dealModalTab === 0 && (
            <Box>
              {/* Lead Search */}
              <Autocomplete
                freeSolo
                options={leadSearchResults}
                getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
                loading={searchingLeads}
                value={selectedLead}
                onChange={(_, newValue) => {
                  if (typeof newValue !== 'string') {
                    setSelectedLead(newValue);
                  }
                }}
                onInputChange={(_, value) => {
                  searchLeads(value);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Buscar lead por nome, email ou telefone"
                    placeholder="Digite para buscar..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: '#9ca3af' }} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      endAdornment: (
                        <>
                          {searchingLeads ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
                    <Avatar sx={{
                      width: 36,
                      height: 36,
                      fontSize: 13,
                      fontWeight: 700,
                      background: getAvatarColor(option.name)
                    }}>
                      {getInitials(option.name)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                          {option.name}
                        </Typography>
                        {option.has_conversation && (
                          <Chip
                            icon={<ChatIcon sx={{ fontSize: 12 }} />}
                            label="Conversa ativa"
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: 10,
                              background: 'rgba(34,197,94,0.1)',
                              color: '#16a34a',
                              '& .MuiChip-icon': { color: '#16a34a' }
                            }}
                          />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, mt: 0.25 }}>
                        {option.phone && (
                          <Typography variant="caption" sx={{ color: '#6b7280' }}>
                            {option.phone}
                          </Typography>
                        )}
                        {option.email && (
                          <Typography variant="caption" sx={{ color: '#6b7280' }}>
                            {option.email}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                )}
                sx={{ mb: 2 }}
              />

              {/* Selected Lead Info */}
              {selectedLead && (
                <Box sx={{
                  p: 2,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, rgba(34,197,94,0.08), rgba(34,197,94,0.02))',
                  border: '1px solid rgba(34,197,94,0.2)',
                  mb: 2
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                    <Avatar sx={{
                      width: 48,
                      height: 48,
                      fontSize: 16,
                      fontWeight: 700,
                      background: getAvatarColor(selectedLead.name)
                    }}>
                      {getInitials(selectedLead.name)}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
                        {selectedLead.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6b7280' }}>
                        {selectedLead.company || 'Sem empresa'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 3 }}>
                    {selectedLead.phone && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PhoneIcon sx={{ fontSize: 14, color: '#6b7280' }} />
                        <Typography variant="caption" sx={{ color: '#6b7280' }}>
                          {selectedLead.phone}
                        </Typography>
                      </Box>
                    )}
                    {selectedLead.email && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <EmailIcon sx={{ fontSize: 14, color: '#6b7280' }} />
                        <Typography variant="caption" sx={{ color: '#6b7280' }}>
                          {selectedLead.email}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  {selectedLead.has_conversation && (
                    <Alert
                      severity="success"
                      icon={<ChatIcon sx={{ fontSize: 18 }} />}
                      sx={{ mt: 1.5, py: 0, fontSize: 13 }}
                    >
                      Este lead já possui uma conversa ativa que será vinculada automaticamente
                    </Alert>
                  )}
                  {selectedLead.tags && selectedLead.tags.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 1.5 }}>
                      {selectedLead.tags.map((tag) => (
                        <Chip
                          key={tag.id}
                          label={tag.name}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: 10,
                            background: `${tag.color}20`,
                            color: tag.color,
                            border: `1px solid ${tag.color}40`
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Deal Value */}
              <TextField
                fullWidth
                label="Valor da Negociação (R$)"
                type="number"
                value={dealValue || ''}
                onChange={(e) => setDealValue(parseFloat(e.target.value) || 0)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>
                }}
                sx={{ mb: 2 }}
              />

              {/* Notes */}
              <TextField
                fullWidth
                label="Observações"
                multiline
                rows={2}
                value={dealNotes}
                onChange={(e) => setDealNotes(e.target.value)}
                placeholder="Adicione observações sobre esta negociação..."
              />
            </Box>
          )}

          {/* Tab 1: Create New Lead */}
          {dealModalTab === 1 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, color: '#6b7280' }}>
                Preencha os dados do novo lead
              </Typography>

              <TextField
                fullWidth
                label="Nome *"
                value={newLeadForm.name}
                onChange={(e) => setNewLeadForm({ ...newLeadForm, name: e.target.value })}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: '#9ca3af', fontSize: 20 }} />
                    </InputAdornment>
                  )
                }}
              />

              <TextField
                fullWidth
                label="WhatsApp/Telefone *"
                value={newLeadForm.phone}
                onChange={(e) => setNewLeadForm({ ...newLeadForm, phone: e.target.value })}
                placeholder="Ex: 5511999999999"
                helperText="Formato: código do país + DDD + número (ex: 55 + 11 + 999999999)"
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <WhatsAppIcon sx={{ color: '#25d366', fontSize: 20 }} />
                    </InputAdornment>
                  )
                }}
              />

              <TextField
                fullWidth
                label="Email"
                type="email"
                value={newLeadForm.email}
                onChange={(e) => setNewLeadForm({ ...newLeadForm, email: e.target.value })}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: '#9ca3af', fontSize: 20 }} />
                    </InputAdornment>
                  )
                }}
              />

              <TextField
                fullWidth
                label="Empresa"
                value={newLeadForm.company}
                onChange={(e) => setNewLeadForm({ ...newLeadForm, company: e.target.value })}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BusinessIcon sx={{ color: '#9ca3af', fontSize: 20 }} />
                    </InputAdornment>
                  )
                }}
              />

              <Divider sx={{ my: 2 }} />

              {/* Deal Value */}
              <TextField
                fullWidth
                label="Valor da Negociação (R$)"
                type="number"
                value={dealValue || ''}
                onChange={(e) => setDealValue(parseFloat(e.target.value) || 0)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>
                }}
                sx={{ mb: 2 }}
              />

              {/* Notes */}
              <TextField
                fullWidth
                label="Observações"
                multiline
                rows={2}
                value={dealNotes}
                onChange={(e) => setDealNotes(e.target.value)}
                placeholder="Adicione observações sobre esta negociação..."
                sx={{ mt: 2 }}
              />
            </Box>
          )}
        </DialogContent>

        {/* Actions */}
        <DialogActions sx={{
          p: 2,
          borderTop: '1px solid rgba(0,0,0,0.06)',
          background: 'rgba(0,0,0,0.02)'
        }}>
          <Button
            onClick={() => {
              setShowOpportunityModal(false);
              resetNewDealModal();
            }}
            sx={{ textTransform: 'none' }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateNewDeal}
            disabled={
              creatingDeal ||
              (dealModalTab === 0 && !selectedLead) ||
              (dealModalTab === 1 && (!newLeadForm.name || !newLeadForm.phone))
            }
            startIcon={creatingDeal ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              '&:hover': {
                background: 'linear-gradient(135deg, #4f46e5, #7c3aed)'
              }
            }}
          >
            {creatingDeal ? 'Criando...' : 'Criar Negociação'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deal Detail Modal */}
      <DealDetailModal
        open={showDetailModal}
        opportunity={selectedOpportunity}
        stages={stages}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedOpportunity(null);
        }}
        onUpdate={() => {
          if (selectedPipeline) {
            loadOpportunities(selectedPipeline);
            loadStages(selectedPipeline);
          }
        }}
        onDelete={handleDeleteOpportunity}
      />

      {/* Edit Modal */}
      <Dialog
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, ...glassStyle } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(0,0,0,0.05)', fontWeight: 700 }}>
          Editar Oportunidade
        </DialogTitle>
        <form onSubmit={handleUpdateOpportunity}>
          <DialogContent sx={{ pt: 3 }}>
            <TextField
              fullWidth
              label="ID do Lead"
              type="number"
              value={opportunityForm.lead_id}
              onChange={(e) => setOpportunityForm({ ...opportunityForm, lead_id: e.target.value })}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Valor Total (R$)"
              type="number"
              value={opportunityForm.total_value}
              onChange={(e) => setOpportunityForm({ ...opportunityForm, total_value: parseFloat(e.target.value) || 0 })}
              required
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
              <TextField
                label="Desconto (R$)"
                type="number"
                value={opportunityForm.discount}
                onChange={(e) => setOpportunityForm({ ...opportunityForm, discount: parseFloat(e.target.value) || 0 })}
              />
              <TextField
                label="Acréscimo (R$)"
                type="number"
                value={opportunityForm.addition}
                onChange={(e) => setOpportunityForm({ ...opportunityForm, addition: parseFloat(e.target.value) || 0 })}
              />
            </Box>
            <TextField
              fullWidth
              label="Frete (R$)"
              type="number"
              value={opportunityForm.freight}
              onChange={(e) => setOpportunityForm({ ...opportunityForm, freight: parseFloat(e.target.value) || 0 })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Notas"
              multiline
              rows={3}
              value={opportunityForm.notes}
              onChange={(e) => setOpportunityForm({ ...opportunityForm, notes: e.target.value })}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(0,0,0,0.05)' }}>
            <Button onClick={() => setShowEditModal(false)}>Cancelar</Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                '&:hover': { background: 'linear-gradient(135deg, #d97706, #b45309)' }
              }}
            >
              Salvar
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Add Product Modal */}
      <Dialog
        open={showProductModal}
        onClose={() => setShowProductModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, ...glassStyle } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(0,0,0,0.05)', fontWeight: 700 }}>
          Adicionar Produto
        </DialogTitle>
        <form onSubmit={handleAddProduct}>
          <DialogContent sx={{ pt: 3 }}>
            <TextField
              fullWidth
              label="Nome"
              value={productForm.name}
              onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Descrição"
              multiline
              rows={2}
              value={productForm.description}
              onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
              <TextField
                label="SKU"
                value={productForm.sku}
                onChange={(e) => setProductForm({ ...productForm, sku: e.target.value })}
              />
              <TextField
                label="Preço (R$)"
                type="number"
                value={productForm.price}
                onChange={(e) => setProductForm({ ...productForm, price: parseFloat(e.target.value) || 0 })}
                required
              />
            </Box>
            <TextField
              fullWidth
              label="Quantidade"
              type="number"
              value={productForm.quantity}
              onChange={(e) => setProductForm({ ...productForm, quantity: parseInt(e.target.value) || 1 })}
              required
            />
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(0,0,0,0.05)' }}>
            <Button onClick={() => setShowProductModal(false)}>Cancelar</Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                '&:hover': { background: 'linear-gradient(135deg, #16a34a, #15803d)' }
              }}
            >
              Adicionar
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Floating Bulk Action Bar */}
      {selectedOpportunities.size > 0 && (
        <Box sx={{
          position: 'fixed',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(0,0,0,0.1)',
          borderRadius: 3,
          px: 3,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
          zIndex: 1000,
          animation: 'slideUp 0.3s ease-out'
        }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1f2937' }}>
            {selectedOpportunities.size} {selectedOpportunities.size === 1 ? 'negociação selecionada' : 'negociações selecionadas'}
          </Typography>
          <Box sx={{ width: 1, height: 24, background: 'rgba(0,0,0,0.1)' }} />
          <Button
            onClick={() => setIsBulkDeleteOpen(true)}
            startIcon={<DeleteIcon />}
            sx={{
              px: 2.5,
              py: 0.75,
              background: '#ef4444',
              color: '#fff',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 13,
              '&:hover': {
                background: '#dc2626'
              }
            }}
          >
            Excluir
          </Button>
          <Button
            onClick={() => setSelectedOpportunities(new Set())}
            sx={{
              px: 2.5,
              py: 0.75,
              background: 'rgba(0,0,0,0.05)',
              color: '#6b7280',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 13,
              '&:hover': {
                background: 'rgba(0,0,0,0.1)'
              }
            }}
          >
            Cancelar
          </Button>
        </Box>
      )}

      {/* Bulk Delete Modal */}
      <BulkDeleteModal
        isOpen={isBulkDeleteOpen}
        onClose={() => setIsBulkDeleteOpen(false)}
        onConfirm={handleBulkDeleteComplete}
        type="deals"
        selectedIds={Array.from(selectedOpportunities)}
      />
    </Box>
  );
};

export default KanbanBoard;
